import { Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { TooltipModule } from 'primeng/tooltip';
import { ClickableDirective } from '../../shared/directives/clickable.directive';
import {
  BanknoteArrowDown,
  Building2,
  Calculator,
  ChevronsUpDown,
  Clock,
  CreditCard,
  Crown,
  FileSpreadsheet,
  FileText,
  FileUser,
  HandCoins,
  Landmark,
  LayoutDashboard,
  LogOut,
  LucideAngularModule,
  LucideIconData,
  Package,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  ReceiptText,
  RefreshCw,
  ShoppingCart,
  Scale,
  Truck,
  Users,
} from 'lucide-angular';
import { TranslocoService } from '@jsverse/transloco';
import { AuthService } from '../../core/services/authentication/auth-service';
import { AccountantContextService } from '../../core/services/accountant-context.service';
import { CompanyContextService } from '../../core/services/company-context.service';
import { UpgradePromptService } from '../../core/services/upgrade-prompt.service';
import { FeaturePermission } from '../../core/config/feature-permissions';
import { CompaniesService } from '../../features/companies/data/companies.service';
import { OnboardingStatus, UserCompanyMembershipDto } from '../../features/companies/models/company.models';
import { BankFeedBadgeService } from '../../features/bank-integrations/data/bank-feed-badge.service';

type IconId =
  | 'customers'
  | 'suppliers'
  | 'dashboard'
  | 'cube'
  | 'purchases'
  | 'sales'
  | 'expenses'
  | 'accounts'
  | 'document'
  | 'file-user'
  | 'banknote-arrow-down'
  | 'invoices'
  | 'bank-feeds'
  | 'hmrc'
  | 'itsa'
  | 'jesty-tax'
  | 'reports';

interface NavItem {
  /** Transloco çeviri anahtarı (ör. `nav.items.home`). */
  label: string;
  path: string;
  iconId: IconId;
  /** Yalnızca bu ülke kodlarındaki şirketlerde gösterilir; boşsa her ülkede görünür. */
  countries?: string[];
  /** Yalnızca e-fatura destekleyen şirketlerde gösterilir. */
  requiresEInvoicing?: boolean;
  /** Yalnızca Plaid banka entegrasyonu destekleyen (GB) şirketlerde gösterilir. */
  requiresBankFeeds?: boolean;
  /** Yalnızca HMRC MTD VAT (vatReturn) destekleyen (GB) şirketlerde gösterilir. */
  requiresVatReturn?: boolean;
  /** HMRC (MTD) destekleyen şirketlerde gösterilir: VAT (vatReturn) veya Gelir Vergisi (incomeTax). */
  requiresHmrc?: boolean;
  /** Bekleyen banka akışı kaydı rozetini gösterir. */
  showPendingBadge?: boolean;
  /** Navigasyon rozeti: 'soon' = yakında (kilitli), 'upgrade' = paket yükseltme gerektirir (kilitli). */
  badge?: 'soon' | 'upgrade';
  /** Şirket özellik izin eşlemesi; izin yoksa öğe 'upgrade' rozetiyle kilitlenir. */
  permission?: FeaturePermission;
}

interface NavSection {
  /** Transloco çeviri anahtarı (ör. `nav.sections.general`). */
  header: string;
  items: NavItem[];
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, LucideAngularModule, TooltipModule, TranslocoModule, ClickableDirective],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
  private readonly authService = inject(AuthService);
  private readonly accountantCtx = inject(AccountantContextService);
  private readonly companyContext = inject(CompanyContextService);
  private readonly upgradePrompt = inject(UpgradePromptService);
  private readonly companiesService = inject(CompaniesService);
  private readonly bankFeedBadge = inject(BankFeedBadgeService);
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  mobile = input(false);
  collapsed = input(false);
  toggleCollapse = output<void>();

  protected readonly isDesktopCollapsed = computed(() => !this.mobile() && this.collapsed());

  protected readonly userMenuOpen = signal(false);
  protected readonly companies = signal<UserCompanyMembershipDto[]>([]);

  protected readonly userName = computed(() => {
    const user = this.authService.getCurrentUser();
    return user?.fullName ?? this.transloco.translate('nav.userMenu.defaultUser');
  });

  protected readonly userInitials = computed(() => {
    const name = this.userName();
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  });

  protected readonly selectedCompanyName = computed(() => {
    const id = this.companyContext.selectedCompanyId();
    const match = this.companies().find(c => c.publicId === id);
    return match?.name ?? '';
  });

  protected readonly brandIcon = Building2;
  protected readonly expandSidebarIcon = PanelLeftOpen;
  protected readonly collapseSidebarIcon = PanelLeftClose;
  protected readonly userMenuIcon = ChevronsUpDown;
  protected readonly logOutIcon = LogOut;
  protected readonly plusIcon = Plus;
  protected readonly refreshIcon = RefreshCw;
  protected readonly pendingIcon = Clock;
  protected readonly accountantPanelIcon = Scale;
  protected readonly upgradeIcon = Crown;

  protected readonly isAccountant = this.accountantCtx.isAccountant;

  protected readonly iconMap: Record<IconId, LucideIconData> = {
    dashboard: LayoutDashboard,
    customers: Users,
    suppliers: Truck,
    cube: Package,
    purchases: ShoppingCart,
    sales: HandCoins,
    expenses: ReceiptText,
    accounts: CreditCard,
    document: FileText,
    'file-user': FileUser,
    'banknote-arrow-down': BanknoteArrowDown,
    invoices: FileText,
    'bank-feeds': Landmark,
    hmrc: Landmark,
    itsa: Landmark,
    'jesty-tax': Calculator,
    reports: FileSpreadsheet,
  };

  private readonly allNavSections: NavSection[] = [
    {
      header: '',
      items: [
        { label: 'nav.items.hmrc', path: '/app/hmrc', iconId: 'hmrc', requiresHmrc: true, permission: { category: 'hmrc-tax' } },
/*         { label: 'nav.items.jestyTax', path: '/app/jesty-tax', iconId: 'jesty-tax', countries: ['GB'], permission: { category: 'jesty-tax' } },
 */      ],
    },
    {
      header: 'nav.sections.general',
      items: [
        { label: 'nav.items.home', path: '/dashboard', iconId: 'dashboard' },
        { label: 'nav.items.reports', path: '/reports', iconId: 'reports', permission: { category: 'general' } },
      ],
    },
    {
      header: 'nav.sections.finance',
      items: [
        { label: 'nav.items.sales', path: '/sales', iconId: 'sales', permission: { category: 'finance', child: 'income' } },
        { label: 'nav.items.purchases', path: '/purchases', iconId: 'purchases', permission: { category: 'finance', child: 'expenses' } },
        { label: 'nav.items.incomingInvoices', path: '/incoming-invoices', iconId: 'invoices', requiresEInvoicing: true },
        { label: 'nav.items.expenses', path: '/expenses', iconId: 'banknote-arrow-down', permission: { category: 'finance', child: 'expenses' } },

      ],
    },
    {
      header: 'nav.sections.operations',
      items: [
        { label: 'nav.items.customers', path: '/customers', iconId: 'customers', permission: { category: 'operations', child: 'customers' } },
        { label: 'nav.items.suppliers', path: '/suppliers', iconId: 'suppliers', permission: { category: 'operations', child: 'suppliers' } },
        { label: 'nav.items.products', path: '/products', iconId: 'cube', permission: { category: 'operations', child: 'products' } },
        { label: 'nav.items.employees', path: '/employees', iconId: 'file-user' },
      ],
    },
    {
      header: 'nav.sections.cash',
      items: [
        { label: 'nav.items.accounts', path: '/accounts', iconId: 'accounts', permission: { category: 'cash-management', child: 'bank-accounts' } },
        { label: 'nav.items.bankFeed', path: '/bank-integrations/bank-feed', iconId: 'bank-feeds', /* requiresBankFeeds: true, showPendingBadge: true, */badge: 'soon', permission: { category: 'cash-management', child: 'bank-feed' } },
      ],
    },
  ];

  protected readonly pendingBankFeedCount = this.bankFeedBadge.pendingCount;

  protected readonly navSections = computed<NavSection[]>(() => {
    const countryCode = this.companyContext.selectedCompanyCountryCode();
    const supportsEInvoicing = this.companyContext.supportsEInvoicing();
    const supportsBankFeeds = this.companyContext.supportsFeature('bankFeeds');
    const supportsVatReturn = this.companyContext.supportsFeature('vatReturn');
    const supportsIncomeTax = this.companyContext.supportsFeature('incomeTax');
    // Tek "HMRC" girişi: VAT veya Gelir Vergisi'nden herhangi biri destekleniyorsa görünür.
    const supportsHmrc = supportsVatReturn || supportsIncomeTax;
    const isUk = countryCode === 'GB';
    return this.allNavSections
      .map((section) => ({
        ...section,
        // Bayrak bilinmiyorsa (null) öğeyi göster — mevcut davranışı korur.
        items: section.items.filter((item) =>
          (!item.countries || !countryCode || item.countries.includes(countryCode)) &&
          (!item.requiresEInvoicing || supportsEInvoicing !== false) &&
          (!item.requiresBankFeeds || supportsBankFeeds) &&
          (!item.requiresVatReturn || supportsVatReturn) &&
          (!item.requiresHmrc || supportsHmrc)),
      }))
      // Özellik izni olmayan öğeleri 'upgrade' rozetiyle kilitle ('soon' rozeti önceliklidir).
      .map((section) => ({
        ...section,
        items: section.items.map((item) => this.applyPermissionBadge(item)),
      }))
      // UK: Finans bölümünü sadeleştir — Satış "Gelirler", Alış/Harcama tek "Giderler" öğesi olur.
      .map((section) => (isUk && section.header === 'nav.sections.finance'
        ? { ...section, items: this.toUkFinanceItems(section.items) }
        : section))
      .filter((section) => section.items.length > 0);
  });

  /** Öğede izin tanımlıysa ve izin yoksa 'upgrade' rozeti ekler; mevcut 'soon' rozeti korunur. */
  private applyPermissionBadge(item: NavItem): NavItem {
    if (item.badge || !item.permission) return item;
    const allowed = this.companyContext.hasFeaturePermission(item.permission.category, item.permission.child);
    return allowed ? item : { ...item, badge: 'upgrade' };
  }

  /**
   * UK Finans bölümü: "Satışlar" → "Gelirler" (/sales), "Giderler" → "Giderler" (/purchases,
   * harcamaları da kapsar). Bağımsız "Harcamalar" öğesi kaldırılır (/expenses UK'de gizli).
   */
  private toUkFinanceItems(items: NavItem[]): NavItem[] {
    return items
      .filter((item) => item.path !== '/expenses')
      .map((item) => {
        if (item.path === '/sales') return { ...item, label: 'nav.items.income' };
        if (item.path === '/purchases') return { ...item, label: 'nav.items.outgoings', iconId: 'banknote-arrow-down' };
        return item;
      });
  }

  constructor() {
    this.companiesService.listForCurrentUser()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(companies => this.companies.set(companies));

    if (this.companyContext.supportsFeature('bankFeeds')) {
      this.bankFeedBadge.refresh();
    }
  }

  /** 'soon' ve 'upgrade' öğeleri kilitlidir: gezinme engellenir. */
  protected isLocked(item: NavItem): boolean {
    return !!item.badge;
  }

  /** Kilitli öğeye tıklama: gezinmeyi engelle; 'upgrade' ise yükseltme modalını aç. */
  protected onLockedClick(item: NavItem, event: Event): void {
    event.preventDefault();
    if (item.badge === 'upgrade') {
      this.upgradePrompt.openPrompt(this.transloco.translate(item.label));
    }
  }

  protected onToggleCollapse(): void {
    if (this.mobile()) {
      return;
    }
    this.toggleCollapse.emit();
  }

  protected toggleUserMenu(): void {
    this.userMenuOpen.update(v => !v);
  }

  protected closeUserMenu(): void {
    this.userMenuOpen.set(false);
  }

  protected isActiveCompany(company: UserCompanyMembershipDto): boolean {
    return company.publicId === this.companyContext.selectedCompanyId();
  }

  /** Kurulumu tamamlanmamış (onboardingStatus = None) şirketler turuncu saat ile işaretlenir. */
  protected needsOnboarding(company: UserCompanyMembershipDto): boolean {
    return (company.onboardingStatus ?? OnboardingStatus.Completed) === OnboardingStatus.None;
  }

  protected switchCompany(company: UserCompanyMembershipDto): void {
    this.companyContext.setSelectedCompanyId(company.publicId);
    this.userMenuOpen.set(false);
    window.location.reload();
  }

  protected navigateDashboard() {
    this.router.navigate(['/']);
  }

  protected goAccountantPanel(): void {
    this.userMenuOpen.set(false);
    void this.router.navigateByUrl('/accountant/dashboard');
  }

  protected addCompany(): void {
    this.userMenuOpen.set(false);
    const user = this.authService.getCurrentUser();
    void this.router.navigate(['/onboarding'], { state: { ownerUserId: user?.publicId } });
  }

  protected signOut(): void {
    this.userMenuOpen.set(false);
    this.authService.logout();
    void this.router.navigateByUrl('/login');
  }
}
